import { ChangeDetectorRef, Component, HostListener, Input, OnInit, QueryList, ViewChildren } from '@angular/core';
import moment from 'moment';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { first } from 'rxjs/operators';
import { CategoryOverTime, cxOneURLs } from 'src/app/cxone/dashboard/modules/category-over-time/category-over-time.model';
import { CategoryOverTimeService } from 'src/app/cxone/dashboard/modules/category-over-time/category-over-time.service';
import { CategorySelectionModalComponent } from 'src/app/cxone/dashboard/modals/category-selection-modal/category-selection-modal.component';
import { CategorySelection, SelectedCategory } from 'src/app/cxone/dashboard/modals/category-selection-modal/category-selection-modal.model';
import { CxonePopoutComponent, PopoutContext } from '../../toolbar-items/cxone-popout/cxone-popout.component';
import { NavigationService } from 'src/app/navigation/navigation.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { ModuleManager } from 'src/shared/models/module-manager';
import { CxoneTeamsComponent } from '../../toolbar-items/cxone-teams/cxone-teams.component';
import { Chart, ChartConfiguration } from 'chart.js';
import { CvConfigurationService } from 'src/app/reusables/services/cv-configuration.service';
import { BaseChartDirective } from 'ng2-charts';
import { ModuleManagerRefreshType } from 'src/shared/models/module-manager/module-manager-options.model';

@Component({
    selector: 'cv-category-over-time',
    templateUrl: './category-over-time.component.html',
    styleUrls: ['./category-over-time.component.scss'],
    providers: [DialogService]
})
export class CategoryOverTimeComponent implements OnInit {
    @Input() moduleManager: ModuleManager;
    @Input() moduleKey: string;
    @ViewChildren(BaseChartDirective) chartObject: QueryList<BaseChartDirective>;

    noData = false;
    public category: CategoryOverTime;
    public canSelectCategory = false;

    public urls: cxOneURLs;
    public newWindowContext: PopoutContext = {
        url: () => this.popoutContextUrl,
        isVisible: () => true // currently always showing
    };
    public chartConfig: ChartConfiguration;

    private popoutContextUrl = "";
    private localizationDict: Map<string, string> = new Map<string, string>();
    private isDataLoading = false;
    private isFirstLoad = true;

    private animateUpdate = true;
    private zoom = {
        max: 2.5,
        min: 1,
        cur: 1,
        baseTicks: 5,
    };

    private modalRef: DynamicDialogRef = null;
    private manualSel = false;
    private selectedCategory: SelectedCategory[] = [];
    private selectedLang = null;

    constructor(private service: CategoryOverTimeService,
        private cvLocalizationService: CVLocalizationService,
        private navSrvc: NavigationService,
        private dialogSrvc: DialogService,
        private CDR: ChangeDetectorRef,
        private config: CvConfigurationService,
    ) {}

    @HostListener('wheel', ['$event']) onMouseWheel(event: WheelEvent) {
        this.handleZoom(event.deltaY);
        return false;
    };

    ngOnInit(): void {
        this.setupModuleManager();
    }

    async setupModuleManager() {
        this.localizationDict = await this.cvLocalizationService.GetSection("Category_Over_Time_Module");

        //Set options
        this.moduleManager.setOptions({
            onSettingsApplied: (settings) => {
                this.loadData();
            },
            hasRefresh: true,
            refreshType: ModuleManagerRefreshType.Auto,
            canRefresh: () => !this.isDataLoading,
            onRefresh: (isAutoRefresh) => {
                if (!isAutoRefresh)
                    this.moduleManager.showLoader = true;
                this.loadData(isAutoRefresh);
            },
            onExport: async (format) => await this.service.exportData(this.moduleKey, format),
        });


        this.moduleManager.addToolbarItem(CxoneTeamsComponent);
        this.moduleManager.addToolbarItem(CxonePopoutComponent, this.newWindowContext);

        //Add settings items
        this.moduleManager.addSetting({
            name: 'selectedCategories',
            getValue: () => this.selectedCategory,
            onSettingLoaded: (value) => { this.selectedCategory = value; },
            defaultValue: []
        });
        this.moduleManager.addSetting({
            name: 'autoSel',
            getValue: () => !this.manualSel,
            onSettingLoaded: (value) => { this.manualSel = !value; },
            defaultValue: true
        });
        this.moduleManager.customSettingsAdded = true;
        this.canSelectCategory = this.moduleManager.hasWriteAccess();

        this.moduleManager.addState({
            name: "selectedCategories",
            autoLoad: true,
            getStateData: () => this.selectedCategory,
            onStateLoaded: stateData => {
                this.selectedCategory = stateData;
            },
        });
        this.moduleManager.addState({
            name: "selectedLang",
            autoLoad: true,
            getStateData: () => this.selectedLang,
            onStateLoaded: stateData => { this.selectedLang = stateData; },
        });
    }

    async loadData(isAutoRefresh: boolean = false) {
        if (this.isDataLoading)
            return;
        this.isDataLoading = true;

        if (!isAutoRefresh) {
            this.moduleManager.showLoader = true;
        }

        const catOverTimeDTO = await this.service.loadData(this.moduleKey, isAutoRefresh, this.isFirstLoad);
        if (!catOverTimeDTO || !catOverTimeDTO?.category?.dateToCount || catOverTimeDTO.category.dateToCount.size === 0) {
            this.noData = true;
            this.CDR.detectChanges();
            this.category = {
                id: "",
                path: "",
                title: "",
                dateToCount: new Map<string, number>(),
                changePercentage: 0,
            };
        }
        else {
            this.noData = false;
            this.CDR.detectChanges();
            this.category = catOverTimeDTO.category;
            if (!(this.category.dateToCount instanceof Map))
                this.category.dateToCount = new Map(Object.entries(this.category.dateToCount));
            this.urls = catOverTimeDTO.urls;

            this.updateChartData();
        }

        this.getSearchURL();
        
        this.isDataLoading = false;
        this.isFirstLoad = false;
        this.moduleManager.setAutoRefreshReady();
        this.moduleManager.showLoader = false;
    }

    getCategoryTitle() {
        if (this.selectedCategory.length > 0 && this.selectedCategory[0].title.trim().length > 0)
            return this.selectedCategory[0].title;

        if (this?.category?.title == null || this.category.title.length === 0) {
            if (this.canSelectCategory)
                return this.localizationDict.get("Select_Category") ?? "Select Category";
            else return "";
        }

        return this.category.title;
    }

    getCategoryID() {
        if (this.selectedCategory.length > 0 && this.selectedCategory[0].id.trim().length > 0)
            return this.selectedCategory[0].id;

        if (this?.category?.id == null || this.category.id.trim().length === 0)
            return "";

        return this.category.id;
    }

    //Category Selection Modal
    showCategoryModal() {
        if (!this.modalRef) {
            let height = "232px";
            let style: any = { "margin-top": "-276px" };
            if (this.manualSel) {
                height = "500px";
                style = {};
            }
            this.modalRef = this.dialogSrvc.open(CategorySelectionModalComponent, {
                width: "540px",
                height,
                data: {
                    moduleKey: this.moduleManager.getModuleKey(),
                    hasAutoSelect: true,
                    manualSelect: this.manualSel,
                    multiSelect: false,
                    selectedCategories: this.selectedCategory,
                    selectedLang: this.selectedLang,
                },
                style,
            });
            this.modalRef.onClose.pipe(first()).subscribe((value: CategorySelection) => {
                if (value != null) {
                    this.manualSel = value.manualSelect;
                    this.selectedLang = value.selectedLang;
                    this.onModalClose(value.selectedCategories);
                }
                this.modalRef = null;
            });
        }
    }

    async onModalClose(value: SelectedCategory[]) {
        if (value == null)
            return;

        this.selectedCategory.splice(0);
        if (value.length > 0)
            this.selectedCategory.push(value[0]);
        await this.moduleManager.saveSettings();
        this.moduleManager.loadSettings(true);
    }

    //#region chart
    getChartConfig(): ChartConfiguration {
        let interactionsStr: string = this.localizationDict.get("Interactions") ?? "Interactions";
        interactionsStr = interactionsStr[0].toUpperCase() + interactionsStr.slice(1);

        return {
            type: "line",
            data: {
                datasets: [],
                labels: []
            },
            options: {
                datasets: {
                    line: {
                        spanGaps: true,
                    },
                },
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    emptyChart: {
                        disableCxone: this.config.isCxOne,
                    },
                    legend: {
                        display: false,
                    },
                    tooltip: {
                        enabled: true,
                        mode: 'point',
                        intersect: true,
                        caretPadding: 10,
                        displayColors: false,
                        backgroundColor: 'rgba(255, 255, 255, 1)',
                        borderWidth: 1,
                        borderColor: 'rgba(208, 210, 211, 1)',
                        titleFont: {
                            family: 'open-sans',
                            size: 11,
                        },
                        titleColor: 'rgba(140, 149, 165, 1)',
                        bodyFont: {
                            family: 'open-sans',
                            size: 13,
                        },
                        bodyColor: 'rgba(46, 46, 46, 1)',
                        callbacks: {
                            title: (item) => moment((item[0].raw as any).x.toString()).format('DD MMM, YYYY'),
                            label: (item) => `${item.parsed.y} ${this.localizationDict.get("Interactions") ?? "interactions"}`,
                        }
                    },
                },
                scales: {
                    x: {
                        grid: {
                            display: false
                        },
                        ticks: {
                            autoSkip: true,
                            maxTicksLimit: Math.round(this.zoom.baseTicks * this.zoom.cur),
                            minRotation: 0,
                            maxRotation: 0
                        }
                    },
                    y: {
                        title: {
                            display: true,
                            text: interactionsStr,
                            font: {
                                family: 'open-sans',
                                size: 14,
                            },
                        },
                        grid: {
                            display: true,
                            color: 'rgba(232, 232, 232, 1)'
                        },
                        border: {
                            display: false,
                            dash: [4, 4],
                        },
                        suggestedMax: 10,
                        suggestedMin: 0,
                    }
                },
                animation: {
                    onProgress: this.drawYAxis.bind(this),
                    onComplete: this.drawYAxis.bind(this),
                },
                onHover: (event, elements, chart) => {
                    const element = chart.getElementsAtEventForMode(event.native, 'index', {intersect: true}, false);
                    if (!element || element.length === 0)
                        event.native.target['style'].cursor = "";
                    else
                        event.native.target['style'].cursor = "pointer";
                },
                onClick: this.onPointClick.bind(this)
            },
        } as ChartConfiguration;
    }

    updateChartData() {
        if (!this.category)
            return;

        this.chartConfig = this.getChartConfig();
        // this.chartApi.canvas.parentElement.style.height = '100%';

        const data: any[] = [];
        const labels: string[] = [];

        this.category.dateToCount.forEach((value, key, map) => {
            data.push({x: moment(key, "YYYY-MM-DD"), y: value});
            labels.push(moment(key, "YYYY-MM-DD").format("MMM DD"));
        });

        this.chartConfig.data.datasets = [{
            data,
            borderColor: 'rgb(0,124,190)',
            fill: true,
            backgroundColor: 'rgba(0,124,190,0.1)',
            tension: 0.1,
            pointBackgroundColor: 'rgba(0, 124, 190, 1)',
            pointHoverBorderColor: 'rgba(0, 124, 190, 0.3)',
            pointHoverBorderWidth: 5
        }];
        this.chartConfig.data.labels = labels;

        this.handleZoom();

        this.animateUpdate = false;
    }

    handleZoom(deltaY: number = 0) {
        if (this.noData || !this.chartObject.last)
            return;

        const temp = this.zoom.cur + 0.1 * ((deltaY > 0) ? -1 : (deltaY < 0) ? 1 : 0);
        if (temp > this.zoom.max)
            this.zoom.cur = this.zoom.max;
        else if (temp < this.zoom.min)
            this.zoom.cur = this.zoom.min;
        else
            this.zoom.cur = temp;

        this.chartObject.last.chart.canvas.parentElement.style.width = `max(100%,${100 * this.zoom.cur}%)`;

        const oldMaxTicks = this.chartConfig.options.scales.x.ticks.maxTicksLimit;
        this.chartConfig.options.scales.x.ticks.maxTicksLimit = Math.round(this.zoom.cur * this.zoom.baseTicks);
        if (Math.abs(this.chartConfig.options.scales.x.ticks.maxTicksLimit - oldMaxTicks) >= 1)
            this.updateChartData(); //recreate chart to update x-axis and repeat the loop :(
        this.chartObject.last.chart.update();
    }

    drawYAxis() {
        if (this.noData)
            return;

        if (this.chartObject?.first?.chart) {
            const scale = window.devicePixelRatio;
            // const width = this.chartConfig.options.scales.x.width - 3;
            // const height = this.chartConfig.options.scales.y.height + this.chartConfig.options.scales.y.top + 10;
            const width = this.chartObject.first.chart.boxes[4].width;
            const height = this.chartObject.first.chart.boxes[4].height;
            const sWidth = width * scale;
            const sHeight = height * scale;
            const yAxis = this.chartObject.first.chart.canvas.getContext("2d");
            //yAxiscanvas
            yAxis.scale(scale, scale);
            yAxis.canvas.width = sWidth;
            yAxis.canvas.height = sHeight;
            yAxis.canvas.style.width = `${width}px`;
            yAxis.canvas.style.height = `${height}px`;

            yAxis.drawImage(this.chartObject.first.chart.canvas, 0, 0, sWidth, sHeight, 0, 0, sWidth, sHeight);
            this.chartObject.first.chart.canvas.style.zIndex = "2";
        }
    }

    async getSearchURL() {
        const envURL = `${this.navSrvc.cxoneTokenUrls?.niceBaseUrl ? this.navSrvc.cxoneTokenUrls.niceBaseUrl : ''}`;

        if (!this.category?.id || !this.moduleManager?.moduleSettings?.selectedDateRange) {
            this.popoutContextUrl = `${envURL}/search/#/search?page=1&sort=1startTime22`;
            return this.newWindowContext.url;
        }

        let range = "";
        let tnf = "";
        const cat: string = this.category.id;

        //calculate range
        const start = this.moduleManager?.moduleSettings.selectedDateRange.startDate ?? null;
        const end = this.moduleManager?.moduleSettings.selectedDateRange.endDate ?? null;
        const days = this.moduleManager?.moduleSettings.selectedDateRange.numberOfDays ?? 0;
        if (start == null || end == null || days === 0)
            return "";
        switch (days) {
            case 7:
                range = `3,${moment().valueOf()}`;
                break;
            case 30:
                range = `7,${moment().valueOf()},30`;
                break;
            default:
                end.setHours(0,0,0,0);
                end.addDays(1).addMilliseconds(-1);
                range = `6,${moment().valueOf()},${moment(start.setHours(0,0,0,0)).valueOf()},${moment(end).valueOf()}`;
                break;
        }

        //generate tnf
        const teams = await this.service.getCXoneTeamNames(this.moduleKey);
        teams.forEach((team) => {
            tnf = `${tnf}%2C${(team.name as string).replace(" ", "%20")}`;
        });

        this.popoutContextUrl = `${envURL}/search/#/search?page=1&sort=1startTime22&range=${range}&tnf=${tnf}&cat=${cat}:A`;
        return this.popoutContextUrl;
    }

    async onPointClick(event, elements) {
        const element = this.chartObject.last.chart.getElementsAtEventForMode(event.native, "index", {intersect: true}, true);
        if (!element || element.length === 0)
            return;

        let envURL = this.urls.niceBaseUrl;
        let range = "";
        let tnf = "";
        const cat = this.category.id;

        if (!envURL.includes("https://"))
            envURL = `https://${envURL}`;

        //calculate range
        const index = element[0].index;
        const dateLabel = [...this.category.dateToCount]?.[index]?.[0] ?? this.chartConfig.data.labels[index].toString();
        const searchDateStart = moment(dateLabel).set("hour", 0).set("minute", 0).set("second", 0).set("millisecond", 0);
        const searchDateEnd = moment(dateLabel).set("hour", 23).set("minute", 59).set("second", 59).set("millisecond", 999);
        range = `6,${moment().valueOf()},${searchDateStart.valueOf()},${searchDateEnd.valueOf()}`;

        //generate tnf
        const teams = await this.service.getCXoneTeamNames(this.moduleKey);
        teams.forEach((team) => {
            tnf = `${tnf}%2C${(team.name as string).replace(" ", "%20")}`;
        });

        if (tnf.startsWith("%2C"))
            tnf = tnf.substring(3);

        window.open(`${envURL}/search/#/search?page=1&sort=1startTime22&range=${range}&tnf=${tnf}&cat=${cat}:A`, "_blank");
    }
    //#endregion
}
